/**
 * useValidatedForm — the universal "never a silent no-op" pattern.
 *
 * Every action button (publish, launch, generate, submit, save, apply…)
 * runs through `submit()`:
 *
 *   1. Required / custom validation runs FIRST — no API call, no job.
 *   2. Invalid → the action does not fire; the page scrolls smoothly to
 *      the FIRST invalid field (with ~20% viewport above it), the field
 *      is highlighted (red border + red label + red inline message,
 *      aria-invalid / aria-describedby) and focused, a summary toast
 *      ("Please complete required fields: A, B", role="alert") slides in
 *      at the top, and the button turns "blocked" (dimmed, tooltip says
 *      why — still clickable: a click re-runs the scroll-to-field).
 *   3. Valid → the action fires (`onSubmit(values)`), `submitting` is
 *      true while it runs.
 *
 * Errors auto-dismiss: as soon as a value changes and the field is
 * valid, its red state goes; when every field is valid the toast closes
 * and the button re-enables — with no extra code in the page.
 *
 *   const form = useValidatedForm({
 *     fields: [
 *       { id: 'productId', label: 'Product', required: true, errorMessage: 'Please select a product' },
 *       { id: 'imageUrl', label: 'Hero image', required: true, errorMessage: 'Please upload a hero image' },
 *       { id: 'budget', label: 'Daily budget', validate: (v) => (Number(v) >= 5 ? null : 'Please enter a daily budget of at least $5') },
 *       { id: 'details', label: 'Details', required: true, when: (values) => values.reason === 'other' },
 *     ],
 *     values: { productId, imageUrl, budget, details, reason },
 *     onSubmit: async (values) => { … },
 *   });
 *
 *   <div ref={form.register('productId')}>…</div>        // scroll / focus target
 *   <TextField {...form.fieldProps('budget')} />           // error + aria attributes
 *   <SubmitButton form={form} label="Publish" />           // or <Button {...form.buttonProps} onClick={form.submit}>
 *   <FormErrorToast form={form} />                         // (ValidatedForm renders it for you)
 *
 * `form.setServerErrors({ channelUrl: 'Please enter a valid URL' })`
 * merges a 400's per-field errors in (they clear when the field changes).
 * `form.validate()` returns the current error map without side effects.
 *
 * Required semantics: '' / whitespace / null / undefined / [] / false
 * (checkbox) / NaN are "missing". `required` can be a boolean or a
 * function of the values; `when` gates the whole field.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const SCROLL_PADDING = 0.2; // 20% of the viewport above the field

export function isMissing(value) {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'boolean') return value === false;
  if (typeof value === 'number') return Number.isNaN(value);
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

function fieldLabel(field) {
  return field.label || field.id;
}

function defaultMessage(field) {
  if (field.errorMessage) return field.errorMessage;
  const verb = field.type === 'select' || field.type === 'picker' ? 'select' : field.type === 'image' || field.type === 'file' ? 'upload' : field.type === 'checkbox' ? 'tick' : 'enter';
  const label = fieldLabel(field);
  return `Please ${verb} ${/^[A-Z][a-z]/.test(label) ? label.charAt(0).toLowerCase() + label.slice(1) : label}.`;
}

/** Error map for `fields` against `values` (pure). */
export function computeErrors(fields, values) {
  const errors = {};
  for (const field of fields || []) {
    if (!field?.id) continue;
    if (typeof field.when === 'function' && !field.when(values)) continue;
    const value = values?.[field.id];
    const required = typeof field.required === 'function' ? field.required(values) : !!field.required;
    if (required && isMissing(value)) { errors[field.id] = defaultMessage(field); continue; }
    if (typeof field.validate === 'function' && !(isMissing(value) && !required)) {
      const msg = field.validate(value, values);
      if (msg) errors[field.id] = String(msg);
    }
  }
  return errors;
}

/** Nearest scrollable ancestor (or null for the window). */
export function scrollParentOf(el) {
  let node = el?.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1) return node;
    node = node.parentElement;
  }
  return null;
}

const FOCUSABLE = 'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

/**
 * Smoothly bring `el` to ~20% below the top of its scroll container and
 * focus the first interactive control inside it (falls back to the
 * container itself). Exported for imperative use outside the hook.
 */
export function scrollToAndFocus(el, { padding = SCROLL_PADDING } = {}) {
  if (!el || typeof window === 'undefined') return;
  try {
    const parent = scrollParentOf(el);
    const rect = el.getBoundingClientRect();
    if (parent) {
      const prect = parent.getBoundingClientRect();
      const top = parent.scrollTop + (rect.top - prect.top) - Math.round(parent.clientHeight * padding);
      parent.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    } else {
      const top = window.scrollY + rect.top - Math.round(window.innerHeight * padding);
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  } catch { try { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { /* no-op */ } }
  const focusTarget = el.matches?.(FOCUSABLE) ? el : el.querySelector?.(FOCUSABLE);
  const target = focusTarget || el;
  try {
    if (target === el && !el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    // After the smooth scroll starts; preventScroll keeps our offset.
    setTimeout(() => { try { target.focus({ preventScroll: true }); } catch { /* no-op */ } }, 60);
  } catch { /* no-op */ }
}

export default function useValidatedForm({ fields = [], values = {}, onSubmit, blockAfterFail = true, padding = SCROLL_PADDING } = {}) {
  const [errors, setErrors] = useState({});
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const nodes = useRef(new Map()); // id → element
  const serverErrors = useRef(new Map()); // id → { message, value }
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const register = useCallback((id) => (el) => {
    if (el) nodes.current.set(id, el); else nodes.current.delete(id);
  }, []);

  const validate = useCallback(() => computeErrors(fieldsRef.current, valuesRef.current), []);

  /** Live validity (no side effects) — for `isValid` / previews. */
  const valuesKey = useMemo(() => { try { return JSON.stringify(values); } catch { return String(Date.now()); } }, [values]);
  const liveErrors = useMemo(() => computeErrors(fields, values), [fields, valuesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-dismiss: after an attempt, every value change re-validates; a
  // field that became valid loses its red state at once, and when the
  // whole form is valid the toast closes and the button re-enables.
  useEffect(() => {
    // Server errors clear when their field changes.
    for (const [id, entry] of serverErrors.current.entries()) {
      if (values?.[id] !== entry.value) serverErrors.current.delete(id);
    }
    if (!attempted && serverErrors.current.size === 0) return;
    const next = { ...(attempted ? computeErrors(fieldsRef.current, values) : {}) };
    for (const [id, entry] of serverErrors.current.entries()) next[id] = entry.message;
    setErrors((prev) => (shallowEqual(prev, next) ? prev : next));
    if (Object.keys(next).length === 0) setToastOpen(false);
  }, [valuesKey, attempted]); // eslint-disable-line react-hooks/exhaustive-deps

  const focusFirst = useCallback((errs) => {
    const order = fieldsRef.current.map((f) => f.id);
    const firstId = order.find((id) => errs[id]) || Object.keys(errs)[0];
    const el = firstId ? nodes.current.get(firstId) : null;
    if (el) scrollToAndFocus(el, { padding });
    else if (firstId) {
      // Not registered: best effort by data-field-id / id.
      const fallback = document.querySelector(`[data-field-id="${firstId}"], #${CSS?.escape ? CSS.escape(firstId) : firstId}`);
      if (fallback) scrollToAndFocus(fallback, { padding });
    }
    return firstId || null;
  }, [padding]);

  const submit = useCallback(async (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (submitting) return false;
    const errs = computeErrors(fieldsRef.current, valuesRef.current);
    for (const [id, entry] of serverErrors.current.entries()) if (valuesRef.current?.[id] === entry.value) errs[id] = entry.message;
    setAttempted(true);
    if (Object.keys(errs).length) {
      setErrors(errs);
      setToastOpen(true);
      focusFirst(errs);
      return false;
    }
    setErrors({});
    setToastOpen(false);
    if (typeof onSubmitRef.current !== 'function') return true;
    setSubmitting(true);
    try {
      await onSubmitRef.current(valuesRef.current);
      return true;
    } finally {
      setSubmitting(false);
    }
  }, [submitting, focusFirst]);

  const setServerErrors = useCallback((map, { focus = true } = {}) => {
    const clean = {};
    for (const [id, message] of Object.entries(map || {})) {
      if (!message || id === '_') continue;
      clean[id] = String(message);
      serverErrors.current.set(id, { message: String(message), value: valuesRef.current?.[id] });
    }
    if (!Object.keys(clean).length) return;
    setAttempted(true);
    setErrors((prev) => ({ ...prev, ...clean }));
    setToastOpen(true);
    if (focus) focusFirst(clean);
  }, [focusFirst]);

  const clearError = useCallback((id) => {
    serverErrors.current.delete(id);
    setErrors((prev) => { if (!(id in prev)) return prev; const next = { ...prev }; delete next[id]; return next; });
  }, []);

  const dismissToast = useCallback(() => setToastOpen(false), []);
  const reset = useCallback(() => { setErrors({}); setAttempted(false); setToastOpen(false); serverErrors.current.clear(); }, []);

  const errorLabels = useMemo(() => {
    const byId = new Map(fields.map((f) => [f.id, f]));
    return Object.keys(errors).map((id) => fieldLabel(byId.get(id) || { id }));
  }, [errors, fields]);
  const hasErrors = errorLabels.length > 0;
  const firstErrorId = fields.map((f) => f.id).find((id) => errors[id]) || Object.keys(errors)[0] || null;
  const blockedReason = hasErrors
    ? (errors[firstErrorId] || 'Please complete the required fields.').replace(/\.$/, '') + (errorLabels.length > 1 ? ` (and ${errorLabels.length - 1} more)` : '')
    : null;

  /**
   * For Polaris TextField / Select / Checkbox / ChoiceList: `error: true`
   * paints Polaris's red state WITHOUT its own message — <ValidatedField>
   * renders the single inline message (role="alert", aria-describedby
   * target), so the merchant never reads the same error twice.
   */
  const fieldProps = useCallback((id) => {
    const error = errors[id] || null;
    return { id, error: error ? true : undefined };
  }, [errors]);

  /** For raw <input>/<select>/<textarea>: aria attributes pointing at <ValidatedField>'s inline error. */
  const inputProps = useCallback((id) => {
    const error = errors[id] || null;
    return {
      id,
      'aria-invalid': error ? 'true' : undefined,
      'aria-describedby': error ? `${id}-error` : undefined,
      'data-invalid': error ? 'true' : undefined,
    };
  }, [errors]);

  const buttonProps = useMemo(() => ({
    onClick: submit,
    loading: submitting,
    // Never a hard `disabled` (that would swallow the click): the blocked
    // look + tooltip explain why, the click re-runs scroll-to-field.
    disabled: false,
    'aria-disabled': blockAfterFail && hasErrors ? 'true' : undefined,
    'data-blocked': blockAfterFail && hasErrors ? 'true' : undefined,
    title: blockAfterFail && hasErrors ? blockedReason : undefined,
  }), [submit, submitting, hasErrors, blockedReason, blockAfterFail]);

  return {
    errors, liveErrors, attempted, submitting, hasErrors, isValid: Object.keys(liveErrors).length === 0,
    submit, validate, register, fieldProps, inputProps, buttonProps, blocked: blockAfterFail && hasErrors, blockedReason,
    toast: toastOpen && hasErrors ? { message: `Please complete required fields: ${errorLabels.join(', ')}`, fields: errorLabels } : null,
    dismissToast, setServerErrors, clearError, reset, focusFirst: () => focusFirst(errors),
  };
}

function shallowEqual(a, b) {
  const ka = Object.keys(a); const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => a[k] === b[k]);
}
